/* eslint-disable prettier/prettier */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";

import CommonStoreHeader from "components/CommonStoreHeader";
import CommonFooter from "components/CommonFooter";
import AppLoader from "components/AppLoader";
import { setLayout, useMaterialUIController } from "context";
import fetchWithAuth from "layouts/tables/data/api";
import appSettings from "appsettings.json";
import productPlaceholder from "assets/images/product-placeholder.svg";

const toSafeNumber = (value) => {
  const parsed = typeof value === "string" ? Number(value.replace(/[^0-9.-]/g, "")) : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value) =>
  `Rs ${toSafeNumber(value).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

function MyOrders() {
  const [, dispatch] = useMaterialUIController();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);
  const [expandedOrders, setExpandedOrders] = useState({});

  const toggleOrderItems = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  useEffect(() => {
    setLayout(dispatch, "page");
    return () => setLayout(dispatch, "dashboard");
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;

    const loadOrders = async () => {
      try {
        setIsLoading(true);
        setError("");

        const response = await fetchWithAuth(
          `${appSettings.baseUrl}${appSettings.endpoints.getMyOrders}`,
          { method: "GET" }
        );
        if (!response.ok) {
          throw new Error("Failed to load orders.");
        }

        const data = await response.json();
        if (!isMounted) return;
        setOrders(Array.isArray(data) ? data : []);
      } catch (loadError) {
        if (isMounted) {
          setError(loadError.message || "Failed to load orders.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadOrders();
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f3f4f6" }}>
      <CommonStoreHeader />

      <Box sx={{ px: { xs: 1.5, sm: 2.5, md: 3.5 }, py: 3 }}>
        <Typography
          sx={{ fontWeight: 900, fontSize: { xs: "1.35rem", md: "1.8rem" }, color: "#7f1d1d" }}
        >
          My Orders
        </Typography>
        <Typography sx={{ color: "#64748b", mt: 0.3, mb: 2.1 }}>
          View all orders you have placed.
        </Typography>

        {isLoading ? (
          <Box
            sx={{ minHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <AppLoader size={42} />
          </Box>
        ) : error ? (
          <Card sx={{ p: 2, border: "1px solid #fecaca", borderRadius: 2 }}>
            <Typography sx={{ color: "#b91c1c", fontWeight: 700 }}>{error}</Typography>
          </Card>
        ) : orders.length === 0 ? (
          <Card sx={{ p: 2.4, border: "1px solid #e2e8f0", borderRadius: 2 }}>
            <Typography sx={{ color: "#64748b", fontWeight: 600 }}>No orders found.</Typography>
          </Card>
        ) : (
          <Stack spacing={2}>
            {orders.map((order) => (
              <Card
                key={`order-${order.orderId}`}
                sx={{ p: 2, border: "1px solid #e2e8f0", borderRadius: 2 }}
              >
                {(() => {
                  const isExpanded = !!expandedOrders[order.orderId];
                  return (
                    <>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        justifyContent="space-between"
                        spacing={1.2}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 800, color: "#7f1d1d" }}>
                            Order #{order.orderId}
                          </Typography>
                          <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>
                            {new Date(order.orderDate).toLocaleString()}
                          </Typography>
                          <Typography sx={{ color: "#0f172a", fontSize: "0.92rem", mt: 0.3 }}>
                            Status: <strong>{order.status}</strong>
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
                          <Typography sx={{ fontWeight: 800, color: "#15803d" }}>
                            {formatCurrency(order.grandTotal ?? order.totalAmount)}
                          </Typography>
                          <Typography sx={{ color: "#64748b", fontSize: "0.86rem", mt: 0.2 }}>
                            Item total: {formatCurrency(order.itemTotal)}
                          </Typography>
                          <Typography sx={{ color: "#64748b", fontSize: "0.86rem" }}>
                            Delivery fee: {formatCurrency(order.deliveryFee)}
                          </Typography>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.2}
                            justifyContent={{ xs: "flex-start", md: "flex-end" }}
                          >
                            <Typography sx={{ color: "#64748b", fontSize: "0.9rem" }}>
                              Items: {order.itemCount}
                            </Typography>
                            <IconButton
                              onClick={() => toggleOrderItems(order.orderId)}
                              size="small"
                              aria-label={isExpanded ? "Hide order items" : "Show order items"}
                              sx={{ color: "#7f1d1d", p: 0.4 }}
                            >
                              <Typography sx={{ fontWeight: 900, lineHeight: 1 }}>
                                {isExpanded ? "▲" : "▼"}
                              </Typography>
                            </IconButton>
                          </Stack>
                          {order.invoiceUrl && (
                            <Button
                              component="a"
                              href={order.invoiceUrl}
                              target="_blank"
                              rel="noreferrer"
                              size="small"
                              variant="outlined"
                              sx={{
                                mt: 0.8,
                                borderColor: "#b71c2a",
                                color: "#b71c2a",
                                fontWeight: 700,
                              }}
                            >
                              View Invoice
                            </Button>
                          )}
                        </Box>
                      </Stack>

                      {isExpanded && <Divider sx={{ my: 1.2 }} />}

                      {isExpanded && (
                        <Stack spacing={1}>
                          {(Array.isArray(order.items) ? order.items : []).map((item) => {
                            const imageUrl =
                              Array.isArray(item.imageUrls) && item.imageUrls.length > 0
                                ? item.imageUrls[0].startsWith("http")
                                  ? item.imageUrls[0]
                                  : `${appSettings.baseUrl}${
                                      item.imageUrls[0].startsWith("/")
                                        ? item.imageUrls[0]
                                        : `/${item.imageUrls[0]}`
                                    }`
                                : productPlaceholder;

                            return (
                              <Stack
                                key={`order-${order.orderId}-item-${item.listingId}-${item.title}`}
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ border: "1px solid #eef2f7", borderRadius: 1.4, p: 1 }}
                              >
                                <Stack direction="row" spacing={1.1} alignItems="center">
                                  <Box
                                    component="img"
                                    src={imageUrl}
                                    alt={item.title}
                                    onError={(event) => {
                                      event.currentTarget.onerror = null;
                                      event.currentTarget.src = productPlaceholder;
                                    }}
                                    sx={{
                                      width: 52,
                                      height: 52,
                                      objectFit: "cover",
                                      borderRadius: 1.1,
                                    }}
                                  />
                                  <Box>
                                    <Typography sx={{ fontWeight: 700 }}>{item.title}</Typography>
                                    <Typography sx={{ color: "#64748b", fontSize: "0.86rem" }}>
                                      Qty: {item.quantity}
                                    </Typography>
                                  </Box>
                                </Stack>
                                <Typography sx={{ fontWeight: 800, color: "#15803d" }}>
                                  {formatCurrency(item.lineTotal)}
                                </Typography>
                              </Stack>
                            );
                          })}
                        </Stack>
                      )}
                    </>
                  );
                })()}
              </Card>
            ))}
          </Stack>
        )}

        <Button
          component={Link}
          to="/"
          variant="text"
          sx={{ mt: 1.5, color: "#7f1d1d", fontWeight: 700 }}
        >
          Continue Shopping
        </Button>
      </Box>

      <CommonFooter />
    </Box>
  );
}

export default MyOrders;
